import { useEffect, useRef, useState } from 'react';

const slides = [
  {
    src: 'https://public.readdy.ai/ai/img_res/bd406b7feca936462f2ad7b7a789bcd3.jpg',
    label: 'Kitchen Remodeling',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=luxury%20master%20bathroom%20remodel%20freestanding%20soaking%20tub%20marble%20tile%20walk%20in%20shower%20double%20vanity%20warm%20ambient%20lighting%20high%20end%20residential%20renovation%20spa%20like%20atmosphere%20professional%20photography%20dramatic%20cinematic&width=1920&height=1080&seq=culp-hero-02&orientation=landscape',
    label: 'Bathroom Remodeling',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=finished%20basement%20entertainment%20room%20luxury%20home%20bar%20custom%20built%20ins%20recessed%20lighting%20warm%20wood%20tones%20high%20end%20residential%20renovation%20professional%20photography%20cinematic%20dramatic%20lighting%20rich%20interior&width=1920&height=1080&seq=culp-hero-03&orientation=landscape',
    label: 'Basement Finishing',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=luxury%20home%20renovation%20living%20room%20open%20concept%20hardwood%20floors%20custom%20millwork%20coffered%20ceiling%20warm%20lighting%20high%20end%20residential%20remodel%20professional%20photography%20cinematic%20dramatic%20depth&width=1920&height=1080&seq=culp-hero-04&orientation=landscape',
    label: 'Home Improvements',
  },
];

export default function Hero() {
  const [current, setCurrent] = useState(0);
  const [prev, setPrev] = useState<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goTo = (idx: number) => {
    if (idx === current) return;
    setPrev(current);
    setCurrent(idx);
    setTimeout(() => setPrev(null), 1200);
  };

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCurrent((c) => {
        const next = (c + 1) % slides.length;
        setPrev(c);
        setTimeout(() => setPrev(null), 1200);
        return next;
      });
    }, 7000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      className="relative h-screen min-h-[760px] max-h-[1080px] overflow-hidden flex items-center"
    >
      {/* Slideshow Background */}
      {slides.map((slide, i) => (
        <div
          key={i}
          className="absolute inset-0"
          style={{
            opacity: i === current ? 1 : i === prev ? 0 : 0,
            transition: i === current ? 'opacity 1.2s ease-in-out' : i === prev ? 'opacity 1.2s ease-in-out' : 'none',
            zIndex: i === current ? 2 : i === prev ? 1 : 0,
          }}
        >
          <img
            src={slide.src}
            alt={`Culp Home Improvement - ${slide.label}`}
            className="w-full h-full object-cover object-center"
            style={{
              transform: i === current ? 'scale(1.04)' : 'scale(1.0)',
              transition: 'transform 9s ease-out',
            }}
          />
        </div>
      ))}

      {/* Cinematic layered overlays */}
      <div className="absolute inset-0 z-10" style={{ background: 'linear-gradient(105deg, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.65) 45%, rgba(0,0,0,0.18) 100%)' }}></div>
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/75 via-transparent to-black/20"></div>

      {/* Decorative vertical line */}
      <div className="absolute left-0 top-0 bottom-0 w-1 z-20" style={{ background: 'linear-gradient(to bottom, transparent, #C9A87C, transparent)' }}></div>

      {/* Content */}
      <div className="relative z-20 w-full px-8 md:px-16 lg:px-28 pt-32">
        <div className="max-w-[680px]">

          {/* Eyebrow pill */}
          <div
            className="inline-flex items-center gap-3 mb-10"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            <div className="flex items-center gap-2 border border-[#C9A87C]/40 bg-[#C9A87C]/8 backdrop-blur-sm text-white/80 text-[10px] font-bold uppercase tracking-[0.22em] px-5 py-2.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C9A87C] inline-block animate-pulse"></span>
              Johnston, Iowa &bull; Est. Residential Remodeling
            </div>
          </div>

          {/* H1 — large, bold, cinematic */}
          <h1
            className="font-black text-white leading-[1.02] mb-8 tracking-tight"
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: 'clamp(48px, 6.5vw, 82px)',
            }}
          >
            High-End Home<br />
            <span style={{ color: '#C9A87C' }}>Remodeling</span><br />
            Built for Lasting Value
          </h1>

          {/* Divider line */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-px bg-[#C9A87C]"></div>
            <p
              className="text-white/70 text-base md:text-lg leading-relaxed max-w-[480px]"
              style={{ fontFamily: 'Lora, serif' }}
            >
              Culp Home Improvement delivers kitchen, bathroom, and home upgrades with precision craftsmanship, durable materials, and clean execution — every time.
            </p>
          </div>

          {/* Trust micro-badges */}
          <div className="flex flex-wrap items-center gap-5 mb-12">
            {[
              { icon: 'ri-shield-check-fill', label: 'Licensed & Insured' },
              { icon: 'ri-award-fill', label: 'Experienced Craftsmen' },
              { icon: 'ri-user-star-fill', label: 'Owner on Every Job' },
            ].map((item, i) => (
              <span
                key={i}
                className="flex items-center gap-2 text-white/75 text-sm font-semibold"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                <i className={`${item.icon} text-[#C9A87C]`}></i>
                {item.label}
              </span>
            ))}
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => scrollTo('contact')}
              className="inline-flex items-center justify-center gap-2.5 text-white font-bold px-11 py-4.5 text-sm tracking-[0.14em] uppercase transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
              style={{
                fontFamily: 'Montserrat, sans-serif',
                background: '#B8956A',
                paddingTop: '18px',
                paddingBottom: '18px',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#9a7a52'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#B8956A'; }}
            >
              <i className="ri-file-list-3-line"></i>
              Get Free Estimate
            </button>
            <a
              href="tel:5153215711"
              className="inline-flex items-center justify-center gap-2.5 border-2 border-white/35 hover:border-white/70 text-white font-bold px-11 text-sm tracking-[0.14em] uppercase transition-all duration-300 hover:bg-white/8 whitespace-nowrap"
              style={{
                fontFamily: 'Montserrat, sans-serif',
                paddingTop: '18px',
                paddingBottom: '18px',
              }}
            >
              <i className="ri-phone-fill text-[#C9A87C]"></i>
              515-321-5711
            </a>
          </div>
        </div>
      </div>

      {/* Bottom bar — slide controls + label */}
      <div className="absolute bottom-0 left-0 right-0 z-20 flex items-end justify-between px-8 md:px-16 lg:px-28 pb-10">
        {/* Slide dots */}
        <div className="flex items-center gap-3">
          {slides.map((slide, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              title={slide.label}
              className="cursor-pointer transition-all duration-500 rounded-full"
              style={{
                width: i === current ? '40px' : '6px',
                height: '6px',
                background: i === current ? '#C9A87C' : 'rgba(255,255,255,0.3)',
              }}
            />
          ))}
        </div>

        {/* Slide counter + label */}
        <div className="hidden md:flex items-center gap-4">
          <span
            className="text-white/40 text-[11px] font-bold uppercase tracking-[0.2em]"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            {slides[current].label}
          </span>
          <span className="text-[#C9A87C]/50 text-xs" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {String(current + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
          </span>
        </div>
      </div>

      {/* Right side scroll indicator */}
      <div className="absolute right-8 top-1/2 -translate-y-1/2 z-20 hidden lg:flex flex-col items-center gap-3">
        <div className="w-px h-16 bg-gradient-to-b from-transparent via-white/30 to-transparent"></div>
        <span
          className="text-white/30 text-[9px] font-bold uppercase tracking-[0.25em]"
          style={{ fontFamily: 'Montserrat, sans-serif', writingMode: 'vertical-rl' }}
        >
          Scroll
        </span>
        <div className="w-px h-16 bg-gradient-to-b from-transparent via-white/30 to-transparent"></div>
      </div>
    </section>
  );
}
